import React, { useState, useEffect } from 'react';
import { Menu, ChevronDown, Globe, Phone, User } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { languages, currencies } from '../i18n/translations';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LANGS, LANG_META, isLang } from '../mock/seoLang';
import BookingDialog from './BookingDialog';

const Logo = ({ light }) => (
  <a href="/" className="flex items-center gap-2 shrink-0">
    <span data-no-translate className={`font-extrabold text-2xl tracking-tight ${light ? 'text-white' : 'text-[color:var(--brand-dark)]'}`}>
      CAB<span style={{ color: 'var(--brand-accent)' }}>.TAXI</span>
    </span>
  </a>
);

const FLAGS = { en: '🇬🇧', de: '🇩🇪', fr: '🇫🇷', it: '🇮🇹', es: '🇪🇸', pt: '🇵🇹', nl: '🇳🇱', pl: '🇵🇱', da: '🇩🇰', sv: '🇸🇪', fi: '🇫🇮', tr: '🇹🇷', ru: '🇷🇺', ar: '🇸🇦', he: '🇮🇱', fa: '🇮🇷', ku: 'KU', hi: '🇮🇳', zh: '🇨🇳', ja: '🇯🇵' };

const Header = ({ forceSolid = false }) => {
  const { t, lang, contentLang, changeLang, currency, changeCurrency } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const pickLang = (code) => {
    changeLang(code);
    const parts = location.pathname.split('/').filter(Boolean);
    if (parts[0] && isLang(parts[0])) parts.shift();
    if (parts[0] === 't' && parts[1]) navigate(`/${code}/t/${parts[1]}`);
    else if (parts[0] === 'transfers') navigate(`/${code}/transfers`);
  };
  const [scrolled, setScrolled] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const isSolid = forceSolid || scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const fleetLabel = t('nav.fleet') !== 'nav.fleet' ? t('nav.fleet') : { de: 'Fahrzeuge', en: 'Fleet', fr: 'Véhicules', it: 'Veicoli' }[lang];
  const pricesLabel = t('nav.prices') !== 'nav.prices' ? t('nav.prices') : { de: 'Preise', en: 'Prices', fr: 'Prix', it: 'Prezzi' }[lang];
  const navItems = [
    { label: t('nav.services'), href: '/#services' },
    { label: fleetLabel, to: '/fleet' },
    { label: pricesLabel, to: '/prices' },
    { label: t('nav.destinations'), to: '/cities' },
    { label: 'Blog', to: '/blog' },
    { label: t('nav.help'), href: '/#help' },
  ];

  const activeLang = { flag: FLAGS[contentLang] || '🌐', label: (LANG_META[contentLang] || {}).name || contentLang };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isSolid ? 'bg-white shadow-md py-2' : 'bg-transparent py-4'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-4">
        <Logo light={!isSolid} />

        <nav className="hidden lg:flex items-center gap-7">
          {navItems.map((item) => {
            const cls = `text-sm font-semibold transition-colors hover:text-[color:var(--brand-accent-hover)] ${isSolid ? 'text-[color:var(--brand-dark)]' : 'text-white'}`;
            return item.to
              ? <Link key={item.label} to={item.to} className={cls}>{item.label}</Link>
              : <a key={item.label} href={item.href} className={cls}>{item.label}</a>;
          })}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          {/* Language */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="lang-trigger" className={`flex items-center gap-1.5 text-sm font-semibold px-2.5 py-1.5 rounded-full border transition-colors ${isSolid ? 'text-[color:var(--brand-dark)] border-gray-200 hover:bg-[color:var(--brand-muted)]' : 'text-white border-white/30 hover:bg-white/10'}`}>
                <Globe className="w-4 h-4" /> <span data-testid="lang-active">{activeLang.flag}</span> <span className="hidden md:inline">{activeLang.label}</span> <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="grid grid-cols-2 gap-0.5 w-[340px] max-h-[70vh] overflow-auto" data-testid="lang-menu">
              {LANGS.map((code) => (
                <DropdownMenuItem key={code} onClick={() => pickLang(code)} data-testid={`lang-option-${code}`} className={`cursor-pointer gap-2 ${contentLang === code ? 'bg-[color:var(--brand-muted)] font-bold' : ''}`}>
                  <span className="w-6 text-center text-xs font-bold">{FLAGS[code]}</span> {LANG_META[code].name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Currency */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={`hidden sm:flex items-center gap-1 text-sm font-semibold px-2 py-1.5 rounded-md transition-colors ${isSolid ? 'text-[color:var(--brand-dark)] hover:bg-[color:var(--brand-muted)]' : 'text-white hover:bg-white/10'}`}>
                {currency} <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {currencies.map((c) => (
                <DropdownMenuItem key={c} onClick={() => changeCurrency(c)} className="cursor-pointer">{c}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <a href="#login" className={`hidden md:flex items-center gap-1.5 text-sm font-semibold transition-colors ${isSolid ? 'text-[color:var(--brand-dark)]' : 'text-white'}`}>
            <User className="w-4 h-4" /> {t('nav.login')}
          </a>

          <button type="button" onClick={() => setBookOpen(true)} data-testid="header-book-btn" className="accent-btn hidden sm:inline-flex items-center rounded-full px-5 py-2.5 text-sm font-bold">
            {t('nav.book')}
          </button>

          {/* Mobile */}
          <Sheet>
            <SheetTrigger asChild>
              <button className={`lg:hidden p-2 rounded-md ${isSolid ? 'text-[color:var(--brand-dark)]' : 'text-white'}`} aria-label="Menu">
                <Menu className="w-6 h-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px]">
              <div className="mt-8 flex flex-col gap-1">
                {navItems.map((item) => (
                  item.to
                    ? <Link key={item.label} to={item.to} className="py-3 px-2 text-base font-semibold text-[color:var(--brand-dark)] border-b border-gray-100">{item.label}</Link>
                    : <a key={item.label} href={item.href} className="py-3 px-2 text-base font-semibold text-[color:var(--brand-dark)] border-b border-gray-100">{item.label}</a>
                ))}
                <a href="#login" className="py-3 px-2 text-base font-semibold text-[color:var(--brand-dark)] border-b border-gray-100 flex items-center gap-2"><User className="w-4 h-4" />{t('nav.login')}</a>
                <div className="flex items-center gap-2 mt-4 text-xs font-bold text-gray-500"><Globe className="w-4 h-4" /> {t('nav.language') === 'nav.language' ? 'Language' : t('nav.language')}</div>
                <div className="grid grid-cols-4 gap-2 mt-2" data-testid="lang-menu-mobile">
                  {LANGS.map((code) => (
                    <button key={code} onClick={() => pickLang(code)} className={`py-2 rounded-md text-xs font-semibold border ${contentLang === code ? 'border-[color:var(--brand-dark)] bg-[color:var(--brand-muted)]' : 'border-gray-200'}`} title={LANG_META[code].name}>{FLAGS[code]}</button>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  {currencies.map((c) => (
                    <button key={c} onClick={() => changeCurrency(c)} className={`flex-1 py-2 rounded-md text-sm font-semibold border ${currency === c ? 'border-[color:var(--brand-dark)] bg-[color:var(--brand-muted)]' : 'border-gray-200'}`}>{c}</button>
                  ))}
                </div>
                <button type="button" onClick={() => setBookOpen(true)} data-testid="header-book-btn-mobile" className="accent-btn mt-6 text-center rounded-full px-5 py-3 text-sm font-bold">{t('nav.book')}</button>
                <a href="tel:+41791772424" className="mt-4 flex items-center justify-center gap-2 text-sm font-semibold text-[color:var(--brand-dark)]"><Phone className="w-4 h-4" /> +41 79 177 24 24</a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <BookingDialog open={bookOpen} onOpenChange={setBookOpen} />
    </header>
  );
};

export default Header;
